import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import { AdminModel } from "../../../models/AdminModel";
import { IAdminOrder } from "../types";

interface CheckSalesReceiptModalProps {
  order: IAdminOrder;
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

type BillingReviewReason = NonNullable<IAdminOrder["billingReview"]>["reason"];

const getReviewReasonCopy = (reason?: BillingReviewReason) => {
  // Parser reasons come from the server so admin can see why automation stopped
  // without having to cross-reference backend logs or raw note text.
  switch (reason) {
    case "missing_amount":
      return {
        title: "Review needed: Amount missing",
        body: "The delivery note looks like a check payment, but no reliable amount was extracted. Confirm the amount before creating the Sales Receipt.",
      };
    case "missing_check_number":
      return {
        title: "Review needed: Check number missing",
        body: "The delivery note includes a check amount, but no reliable check number was extracted. Confirm the check number before creating the Sales Receipt.",
      };
    case "amount_mismatch":
      return {
        title: "Review needed: Amount does not match expected total",
        body: "The extracted check amount does not match the expected invoice amount from customer notes. Confirm whether this is a partial payment or an amount that needs manual correction.",
      };
    case "ambiguous":
      return {
        title: "Review needed: Note is ambiguous",
        body: "The delivery note contains check-like information, but the parser could not determine one reliable amount/check combination. Review the note and enter the final values manually.",
      };
    default:
      return null;
  }
};

export const CheckSalesReceiptModal = ({
  order,
  isOpen,
  onClose,
  onSubmitted,
}: CheckSalesReceiptModalProps) => {
  const [checkNumber, setCheckNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [serverErrorMsg, setServerErrorMsg] = useState<string | null>(null);

  const latestNoteText = order.deliveryNotes?.[0]?.notes || "";
  const reviewReasonCopy = getReviewReasonCopy(order.billingReview?.reason);

  useEffect(() => {
    if (!isOpen) return;
    // Seed the form with whatever the parser already extracted so admin usually only needs
    // to fix the missing/ambiguous piece instead of retyping the whole check entry.
    setCheckNumber(order.billingReview?.checkNumber || "");
    setAmount(
      order.billingReview?.amount === undefined ? "" : String(order.billingReview.amount)
    );
    setServerErrorMsg(null);
    setIsLoading(false);
  }, [isOpen, order.billingReview?.amount, order.billingReview?.checkNumber]);

  const handleSubmit = async () => {
    const parsedAmount = Number(amount);
    if (!checkNumber.trim() || !Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setServerErrorMsg("Check number and a valid amount are required");
      return;
    }

    try {
      setIsLoading(true);
      await AdminModel.createCheckSalesReceipt(order._id, {
        checkNumber: checkNumber.trim(),
        amount: parsedAmount,
        noteText: latestNoteText,
      });
      onSubmitted();
      onClose();
    } catch (error: any) {
      setServerErrorMsg(
        error?.response?.data?.message || error?.message || "Failed to create Sales Receipt"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Review Check Payment</DialogTitle>
      <DialogContent dividers>
        {reviewReasonCopy && (
          <Alert severity="warning" style={{ marginBottom: "1rem" }}>
            <strong>{reviewReasonCopy.title}</strong>
            <div>{reviewReasonCopy.body}</div>
          </Alert>
        )}
        <Typography variant="body2" style={{ marginBottom: "1rem" }}>
          Latest delivery note:
        </Typography>
        <Typography
          component="pre"
          style={{ whiteSpace: "pre-wrap", marginBottom: "1rem" }}
        >
          {latestNoteText || "No note available."}
        </Typography>
        <TextField
          margin="dense"
          label="Check Number"
          value={checkNumber}
          onChange={(event) => setCheckNumber(event.target.value)}
          fullWidth
          required
        />
        <TextField
          margin="dense"
          label="Amount"
          type="number"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          fullWidth
          required
        />
        {isLoading && (
          <Alert severity="info" style={{ marginTop: "1rem" }}>
            {/* The API call now includes both QuickBooks creation and customer email delivery,
                so give admin immediate feedback instead of letting the modal feel stuck. */}
            Creating the Sales Receipt and sending the customer email...
          </Alert>
        )}
        {serverErrorMsg && <Alert severity="error">{serverErrorMsg}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={isLoading}>
          {isLoading ? "Sending Sales Receipt..." : "Create Sales Receipt"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
